import { SessionExpireeError, getApi } from '@/lib/api';
import { lireSession } from '@/lib/session';
import type { ControleTraduction } from '@rosetta/shared';
import { useQuery } from '@tanstack/react-query';

/** The query key of the quality control of a text, read from the launch `id` (#10, #34). */
export const cleControle = (id: string) => ['controle', id] as const;

/** How long a complete control stays fresh: the launches of a document may grow (#11). */
export const FRAICHEUR_CONTROLE_MS = 60_000;

/** How long an empty or incomplete read stays fresh: short, never kept for the session (#34). */
export const FRAICHEUR_CONTROLE_VIDE_MS = 5_000;

/**
 * How long a read stays fresh: for a while when it holds a control and left no launch unread, a few
 * seconds when it is empty or incomplete (it may be different a moment later).
 */
export function fraicheurControle(controle: ControleTraduction | null | undefined): number {
  const complet =
    controle !== null &&
    controle !== undefined &&
    controle.noyaux.length > 0 &&
    (controle.noyauxNonLus ?? []).length === 0;
  return complet ? FRAICHEUR_CONTROLE_MS : FRAICHEUR_CONTROLE_VIDE_MS;
}

/**
 * Reads what the quality control of Tahiti says about the text of a translation, once it is
 * finished (`actif`): the control of **every launch of its document**, merged (#34), because one
 * control covers one launch and the page shows every noyau of the text. Used by the analysis only,
 * and an addition to it, never a condition of the page.
 *
 * A failed read is tried once more after the pause of the client (1 s, the default of TanStack Query), then stays an error the screen words as
 * "unreadable" with a "réessayer" (`refetch`), unlike a control that does not exist. A refused
 * session is not retried: the screen goes back to the login, as for the translation. What is
 * empty or incomplete is only fresh for a few seconds, never for the whole session.
 */
export function useLectureControle(id: string, actif: boolean) {
  return useQuery<ControleTraduction | null, Error>({
    queryKey: cleControle(id),
    queryFn: () => {
      const session = lireSession();
      if (session === null) throw new SessionExpireeError();
      return getApi().lireControle(session, id);
    },
    enabled: actif,
    retry: (tentatives, erreur) => tentatives < 1 && !(erreur instanceof SessionExpireeError),
    staleTime: (requete) => fraicheurControle(requete.state.data),
  });
}
